import { useMemo } from 'react';

import { percentageFormatter } from '@/lib/formatting';

import { CHART_RANGE_MINUTES, TIME_FRAMES } from '@/components/Charts/utils';
import { CHART_COLORS } from '@/components/TokenPriceChart/constants';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useMeasure } from '@/hooks/useMeasure';
import { useTokenPriceSeries } from '@/hooks/useTokenPriceSeries';
import { useUSDPrices } from '@/hooks/useUSDPrices';

import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { TimeSeriesAreaChart } from '@/ui/Charts/TimeSeriesChart/TimeSeriesAreaChart';

import type { TokenResult } from '@/utils/0x/token-registry.types';

const CHART_HEIGHT = 56;
const CHART_PADDING = { top: 4, right: 0, bottom: 4, left: 0 };

export function TokenPriceWidget({ token }: { token: TokenResult }) {
  const { formatFiat } = useFiatDisplay();
  const { bounds, nodeRef } = useMeasure<HTMLDivElement>();
  const priceInputs = useMemo(
    () => [{ address: token.address, networkId: token.chainId, issuer: token.issuer }],
    [token],
  );
  const { data: usdPrices } = useUSDPrices(priceInputs);
  const { data: series } = useTokenPriceSeries(token, {
    chartRangeInMinutes: CHART_RANGE_MINUTES.DAILY,
    resolution: TIME_FRAMES.FIFTEEN_MINUTES,
  });

  const priceUsd = usdPrices?.[0]?.priceUsd;
  const openPriceUsd = series[0]?.value;
  const change =
    priceUsd != null && openPriceUsd ? (priceUsd - openPriceUsd) / openPriceUsd : undefined;
  const colors =
    change == null || change === 0
      ? CHART_COLORS.neutral
      : change > 0
        ? CHART_COLORS.positive
        : CHART_COLORS.negative;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-token-gray-300 bg-token-gray-200 pl-3 py-1">
      <TokenAvatar address={token.address} chainId={token.chainId} size={40} />
      <div className="min-w-0 max-w-[50%] shrink-0">
        <p
          className={fontStyles({
            variant: 'LABEL_MD',
            class: 'truncate text-token-gray-1600 mb-1',
          })}
        >
          {token.name}
        </p>
        <p className={fontStyles({ variant: 'BODY_SM', class: 'flex gap-1.5 whitespace-nowrap' })}>
          <span className="truncate text-token-gray-800">{token.symbol}</span>
          <span className="shrink-0 text-token-gray-1600">
            {priceUsd == null ? '-' : formatFiat(priceUsd, true)}
          </span>
          {change != null && (
            <span
              className={`shrink-0 ${change >= 0 ? 'text-token-green-1000' : 'text-token-red-900'}`}
            >
              {percentageFormatter.format(change)}
            </span>
          )}
        </p>
      </div>
      <div
        ref={nodeRef}
        className="min-w-0 flex-1 mask-l-from-60% -ml-2"
        style={{ height: CHART_HEIGHT }}
      >
        {series.length > 1 && bounds.width > 0 && (
          <TimeSeriesAreaChart
            ariaLabel={`${token.symbol} 24h price chart`}
            data={series}
            width={bounds.width}
            height={CHART_HEIGHT}
            padding={CHART_PADDING}
            lineColor={`var(--token-color-${colors.lineColor})`}
            gradientColor={`var(--token-color-${colors.gradientColor})`}
            lineWidth={1.5}
          />
        )}
      </div>
    </div>
  );
}
